import React, { useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaUsers,
  FaClock,
  FaVenusMars,
  FaPhone,
  FaLanguage,
  FaCalendarDay,
  FaMoon,
  FaCoins,
  FaShoppingCart,
  FaCopy,
  FaCheck,
  FaTimes,
  FaBullseye,
} from "react-icons/fa";

const THEMES = [{ bar: "bg-pink-500", pct: "text-pink-600" }];

// ─── Skeleton ─────────────────────────────────────────────────────────────────
export const SkeletonCard = () => (
  <div className="animate-pulse rounded-xl border border-gray-200 bg-white">
    <div className="h-1 w-full rounded-t-xl bg-gray-100" />
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="h-4 w-28 rounded bg-gray-100" />
          <div className="h-5 w-16 rounded-full bg-gray-100" />
        </div>
        <div className="flex gap-1.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 w-8 rounded-lg bg-gray-100" />
          ))}
        </div>
      </div>
      <div className="h-px bg-gray-100" />
      <div className="flex items-end justify-between">
        <div className="h-10 w-20 rounded bg-gray-100" />
        <div className="h-4 w-24 rounded bg-gray-100" />
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-100" />
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-3 w-full rounded bg-gray-100" />
        ))}
      </div>
    </div>
  </div>
);

// ─── Info row ─────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-[12px] text-gray-500">
    <Icon className="mt-0.5 flex-shrink-0 text-[11px] text-gray-400" />
    <span className="leading-snug">{children}</span>
  </div>
);

// ─── Targeting chip ───────────────────────────────────────────────────────────
const TChip = ({ label }) => (
  <span className="inline-block rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
    {label}
  </span>
);

// ─── Targeting group row ──────────────────────────────────────────────────────
const TargetGroup = ({ icon: Icon, title, children }) => (
  <div>
    <div className="mb-1.5 flex items-center gap-1.5">
      <Icon className="text-[10px] text-gray-400" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {title}
      </span>
    </div>
    {children}
  </div>
);

// ─── Targeting Modal ──────────────────────────────────────────────────────────
const TargetingModal = ({ isOpen, onClose, coupon }) => {
  if (!isOpen || !coupon) return null;
  const targeting = coupon.targeting || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="bg-black/25 absolute inset-0 backdrop-blur-[4px]"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              Targeting Rules
            </h2>
            <p className="mt-0.5 font-mono text-xs text-gray-400">
              {coupon.couponCode}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-100 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
          >
            <FaTimes className="text-[10px]" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <div className="space-y-4">
            {targeting.genders?.length > 0 && (
              <TargetGroup icon={FaVenusMars} title="Genders">
                <div className="flex flex-wrap gap-1.5">
                  {targeting.genders.map((g) => (
                    <TChip
                      key={g}
                      label={g.charAt(0).toUpperCase() + g.slice(1)}
                    />
                  ))}
                </div>
              </TargetGroup>
            )}

            {targeting.applicableDays?.length > 0 && (
              <TargetGroup icon={FaCalendarDay} title="Valid Days">
                <div className="flex flex-wrap gap-1.5">
                  {targeting.applicableDays.map((d) => (
                    <TChip key={d} label={d.slice(0, 3)} />
                  ))}
                </div>
              </TargetGroup>
            )}

            {targeting.weekendOnly && (
              <TargetGroup icon={FaMoon} title="Time Restriction">
                <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                  Weekend only (Sat & Sun)
                </span>
              </TargetGroup>
            )}

            {targeting.firstPurchaseOnly && (
              <TargetGroup icon={FaShoppingCart} title="Purchase Restriction">
                <span className="inline-block rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
                  First purchase only
                </span>
              </TargetGroup>
            )}

            {targeting.minimumCoinSpend > 0 && (
              <TargetGroup icon={FaCoins} title="Min. Coin Spend">
                <TChip label={`${targeting.minimumCoinSpend} coins`} />
              </TargetGroup>
            )}

            {targeting.preferredLanguages?.length > 0 && (
              <TargetGroup icon={FaLanguage} title="Languages">
                <div className="flex flex-wrap gap-1.5">
                  {targeting.preferredLanguages.map((l) => (
                    <TChip
                      key={l}
                      label={l.charAt(0).toUpperCase() + l.slice(1)}
                    />
                  ))}
                </div>
              </TargetGroup>
            )}

            {targeting.phoneNumbers?.length > 0 && (
              <TargetGroup icon={FaPhone} title="Whitelisted Numbers">
                <div className="flex flex-wrap gap-1.5">
                  {targeting.phoneNumbers.map((p) => (
                    <TChip key={p} label={p} />
                  ))}
                </div>
              </TargetGroup>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-lg bg-gray-50 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main card ────────────────────────────────────────────────────────────────
const CouponCard = ({ coupon, index, onEdit, onDelete, onToggle }) => {
  const t = THEMES[index % THEMES.length];
  const [copied, setCopied] = useState(false);
  const [targetingModalOpen, setTargetingModalOpen] = useState(false);

  const isActive = coupon.status === "active";
  const targeting = coupon.targeting || {};

  const expiry = new Date(coupon.expiryDate).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const validFrom = coupon.validFrom
    ? new Date(coupon.validFrom).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  const isExpired = new Date(coupon.expiryDate) <= new Date();
  const daysLeft = Math.ceil(
    (new Date(coupon.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
  );
  const isExpiringSoon = !isExpired && daysLeft <= 7;
  const usagePercent = coupon.usageLimit
    ? Math.min(100, Math.round((coupon.usedCount / coupon.usageLimit) * 100))
    : 0;
  const isExhausted = coupon.usedCount >= coupon.usageLimit;

  const hasTargeting =
    targeting.genders?.length > 0 ||
    targeting.preferredLanguages?.length > 0 ||
    targeting.phoneNumbers?.length > 0 ||
    targeting.applicableDays?.length > 0 ||
    targeting.weekendOnly ||
    targeting.firstPurchaseOnly ||
    targeting.minimumCoinSpend > 0;

  // Build a short summary of active targeting rules for the card
  const targetingSummary = [];
  if (targeting.genders?.length > 0)
    targetingSummary.push(
      `${targeting.genders.length} gender${
        targeting.genders.length > 1 ? "s" : ""
      }`
    );
  if (targeting.applicableDays?.length > 0)
    targetingSummary.push(
      `${targeting.applicableDays.length} day${
        targeting.applicableDays.length > 1 ? "s" : ""
      }`
    );
  if (targeting.weekendOnly) targetingSummary.push("weekend");
  if (targeting.preferredLanguages?.length > 0)
    targetingSummary.push(`${targeting.preferredLanguages.length} lang`);
  if (targeting.phoneNumbers?.length > 0)
    targetingSummary.push(
      `${targeting.phoneNumbers.length} number${
        targeting.phoneNumbers.length > 1 ? "s" : ""
      }`
    );
  if (targeting.firstPurchaseOnly) targetingSummary.push("1st purchase");
  if (targeting.minimumCoinSpend > 0)
    targetingSummary.push(`${targeting.minimumCoinSpend} coins`);

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
        {/* Accent bar */}
        <div className={`h-1 w-full ${t.bar}`} />

        <div className="flex flex-col gap-0 p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <button
              onClick={handleCopy}
              title="Copy code"
              className="group flex min-w-0 items-center gap-1.5"
            >
              <p className="truncate font-mono text-[16px] font-bold tracking-wider text-gray-800 transition-colors group-hover:text-gray-500">
                {coupon.couponCode}
              </p>
              {copied ? (
                <FaCheck className="text-emerald-500 flex-shrink-0 text-[10px]" />
              ) : (
                <FaCopy className="flex-shrink-0 text-[10px] text-gray-300 opacity-0 transition-opacity group-hover:opacity-100" />
              )}
            </button>

            <div className="flex flex-shrink-0 gap-1">
              <button
                onClick={() => onToggle(coupon)}
                title={isActive ? "Deactivate" : "Activate"}
                className={`flex h-8 w-8 items-center justify-center rounded-lg border text-sm transition-colors ${
                  isActive
                    ? "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                    : "border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100"
                }`}
              >
                {isActive ? <FaToggleOn /> : <FaToggleOff />}
              </button>
              <button
                onClick={() => onEdit(coupon)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => onDelete(coupon)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                <FaTrash />
              </button>
            </div>
          </div>

          {/* Status badges */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
                isActive
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 bg-gray-100 text-gray-500"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isActive ? "bg-emerald-500" : "bg-gray-400"
                }`}
              />
              {isActive ? "Active" : "Inactive"}
            </span>
            {isExpired && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-medium text-red-600">
                Expired
              </span>
            )}
            {isExpiringSoon && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-600">
                Expires in {daysLeft}d
              </span>
            )}
            {isExhausted && (
              <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-[11px] font-medium text-orange-600">
                Exhausted
              </span>
            )}
          </div>

          <hr className="my-3 border-gray-100" />

          {/* Discount + usage */}
          <div className="flex items-end justify-between">
            <div>
              <span className={`text-3xl font-bold leading-none ${t.pct}`}>
                {coupon.discountPercentage}%
              </span>
              <span className="ml-1 text-xs font-medium text-gray-400">
                off
              </span>
            </div>
            <div className="text-right">
              <p className="mb-0.5 text-[11px] text-gray-400">Usage</p>
              <p className="text-xs font-semibold text-gray-700">
                {coupon.usedCount}
                <span className="font-normal text-gray-400">
                  {" "}
                  / {coupon.usageLimit}
                </span>
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full transition-all ${
                usagePercent >= 90
                  ? "bg-red-500"
                  : usagePercent >= 70
                  ? "bg-amber-500"
                  : t.bar
              }`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>

          <hr className="my-3 border-gray-100" />

          {/* Dates */}
          <div className="space-y-2">
            {validFrom && (
              <InfoRow icon={FaClock}>
                Valid from:{" "}
                <span className="ml-1 font-medium text-gray-700">
                  {validFrom}
                </span>
              </InfoRow>
            )}
            <InfoRow icon={FaCalendarAlt}>
              Expires:{" "}
              <span
                className={`ml-1 font-medium ${
                  isExpired
                    ? "text-red-600"
                    : isExpiringSoon
                    ? "text-amber-600"
                    : "text-gray-700"
                }`}
              >
                {expiry}
              </span>
            </InfoRow>
            <InfoRow icon={FaUsers}>
              {coupon.usedCount} used ·{" "}
              {Math.max(0, coupon.usageLimit - coupon.usedCount)} remaining
            </InfoRow>
          </div>

          {/* Targeting row */}
          <hr className="my-3 border-gray-100" />
          {hasTargeting ? (
            <button
              type="button"
              onClick={() => setTargetingModalOpen(true)}
              className="group flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:border-pink-200 hover:bg-pink-50"
            >
              <div className="flex items-center gap-2">
                <FaBullseye className="text-[11px] text-pink-400" />
                <span className="text-[11px] font-semibold text-gray-600 group-hover:text-pink-700">
                  Targeting rules
                </span>
                <span className="text-[10px] text-gray-400 group-hover:text-pink-500">
                  · {targetingSummary.join(", ")}
                </span>
              </div>
              <span className="text-[10px] font-medium text-gray-400 group-hover:text-pink-500">
                View →
              </span>
            </button>
          ) : (
            <p className="text-[11px] italic text-gray-400">
              No targeting — applies to all users
            </p>
          )}
        </div>
      </div>

      {/* Targeting modal */}
      <TargetingModal
        isOpen={targetingModalOpen}
        onClose={() => setTargetingModalOpen(false)}
        coupon={coupon}
      />
    </>
  );
};

export default CouponCard;
